import { useState, useEffect, useCallback } from 'react';
import {
  Mail, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  Eye, Clock, Inbox, FileText, Settings,
  Zap, ChevronLeft, ChevronRight, X, Save, TestTube,
  Delete, Power, PowerOff, Loader2, Radio, AlertCircle
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import usePageMetaStore from '../../store/pageMetaStore';
import ConfirmModal from '../../components/ConfirmModal';

// PDF Viewer Modal Component
function PDFViewerModal({ facturaId, onClose }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (facturaId) {
      loadPDF();
    }
    return () => {
      // Cleanup blob URL when modal closes
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [facturaId]);

  const loadPDF = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await gastosApi.downloadPDF(facturaId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('Error loading PDF:', err);
      // El responseType es blob, hay que parsear el error como JSON
      let errorMsg = 'Esta factura no tiene PDF asociado';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.error || errorMsg;
        } catch (e) {
          // Si no se puede parsear, usar mensaje default
        }
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!facturaId) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <FileText className="text-primary-600" size={22} />
            <h2 className="text-lg font-bold text-gray-900">PDF de Factura</h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="animate-spin text-primary-600" size={32} />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              {error.includes('no encontrada') ? (
                <>
                  <Delete className="h-12 w-12 text-red-500 mb-4" />
                  <p className="text-lg font-medium text-red-600">Factura Eliminada</p>
                  <p className="text-sm mt-2">Esta factura fue eliminada del sistema.</p>
                </>
              ) : (
                <>
                  <AlertTriangle size={48} className="text-yellow-500 mb-4" />
                  <p className="text-lg font-medium">{error}</p>
                  <p className="text-sm mt-2">El PDF no está disponible para esta factura.</p>
                </>
              )}
            </div>
          ) : (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title="PDF Viewer"
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// Email Configuration Modal Component
function EmailConfigModal({ isOpen, onClose, onSave, currentConfig }) {
  const [formData, setFormData] = useState({
    azure_tenant_id: '',
    azure_client_id: '',
    azure_client_secret: '',
    mailbox_email: '',
    inbox_folder: 'Inbox',
    processed_folder: 'Procesados',
    auto_process: true,
    check_interval_minutes: 15,
    webhook_url: ''
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [deletingWebhook, setDeletingWebhook] = useState(false);

  useEffect(() => {
    if (currentConfig) {
      // Backend returns camelCase, map to snake_case for form
      setFormData({
        azure_tenant_id: currentConfig.azureTenantId || currentConfig.azure_tenant_id || '',
        azure_client_id: currentConfig.azureClientId || currentConfig.azure_client_id || '',
        azure_client_secret: '', // Never prefill secret
        mailbox_email: currentConfig.mailboxEmail || currentConfig.mailbox_email || '',
        inbox_folder: currentConfig.inboxFolder || currentConfig.inbox_folder || 'Inbox',
        processed_folder: currentConfig.processedFolder || currentConfig.processed_folder || 'Procesados',
        auto_process: currentConfig.autoProcess !== undefined ? currentConfig.autoProcess : (currentConfig.auto_process !== false),
        check_interval_minutes: currentConfig.checkIntervalMinutes || currentConfig.check_interval_minutes || 15,
        webhook_url: currentConfig.webhookUrl || currentConfig.webhook_url || ''
      });
    }
  }, [currentConfig]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setTestResult(null);
  };

  const handleTest = async () => {
    if (!formData.azure_tenant_id || !formData.azure_client_id || !formData.mailbox_email) {
      toast.error('Complete los campos requeridos');
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);
      await gastosApi.testGastosEmailConnection();
      setTestResult({ success: true, message: 'Conexion exitosa con Microsoft Graph' });
      toast.success('Conexion exitosa');
    } catch (error) {
      const msg = error.response?.data?.error || 'Error de conexion';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!formData.azure_tenant_id || !formData.azure_client_id || !formData.mailbox_email) {
      toast.error('Complete los campos requeridos');
      return;
    }

    // If editing, secret is optional (keep existing)
    if (!currentConfig && !formData.azure_client_secret) {
      toast.error('El Client Secret es requerido');
      return;
    }

    try {
      setSaving(true);
      // Transform to camelCase for backend
      const payload = {
        azureTenantId: formData.azure_tenant_id,
        azureClientId: formData.azure_client_id,
        azureClientSecret: formData.azure_client_secret,
        mailboxEmail: formData.mailbox_email,
        inboxFolder: formData.inbox_folder,
        processedFolder: formData.processed_folder,
        autoProcess: formData.auto_process,
        checkIntervalMinutes: parseInt(formData.check_interval_minutes),
        webhookUrl: formData.webhook_url
      };
      await gastosApi.saveGastosEmailConfig(payload);
      toast.success('Configuracion guardada');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      setDeleting(true);
      await gastosApi.deleteGastosEmailConfig();
      toast.success('Configuracion eliminada');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggle = async () => {
    try {
      setToggling(true);
      await gastosApi.toggleGastosEmailActive();
      toast.success(currentConfig?.is_active ? 'Importacion desactivada' : 'Importacion activada');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error');
    } finally {
      setToggling(false);
    }
  };

  const handleCreateWebhook = async () => {
    try {
      setCreatingWebhook(true);
      await gastosApi.createWebhook();
      toast.success('Webhook activado correctamente');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear webhook');
    } finally {
      setCreatingWebhook(false);
    }
  };

  const handleDeleteWebhook = async () => {
    try {
      setDeletingWebhook(true);
      await gastosApi.deleteWebhook();
      toast.success('Webhook eliminado');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar webhook');
    } finally {
      setDeletingWebhook(false);
    }
  };

  // Check webhook status
  const webhookActive = currentConfig?.webhookSubscriptionId || currentConfig?.webhook_subscription_id;
  const webhookExpiration = currentConfig?.webhookExpiration || currentConfig?.webhook_expiration;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b">
          <div className="flex items-center gap-3">
            <Mail className="text-primary-600" size={22} />
            <h2 className="text-lg font-bold text-gray-900">Configurar Email para Gastos</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={22} />
          </button>
        </div>

        {/* Body - 2 columns */}
        <div className="p-5">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-5">
            <p className="text-sm text-blue-800">
              <strong>Email Separado:</strong> Configure una cuenta Microsoft 365 exclusiva para recibir facturas de proveedores.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Azure Credentials */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm border-b pb-2">
                <Settings size={16} />
                Credenciales Azure AD (Microsoft 365)
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="azure_tenant_id"
                  value={formData.azure_tenant_id}
                  onChange={handleChange}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="azure_client_id"
                  value={formData.azure_client_id}
                  onChange={handleChange}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Secret {!currentConfig && <span className="text-red-500">*</span>}
                  {currentConfig && <span className="text-gray-400 text-xs ml-1">(vacio = mantener actual)</span>}
                </label>
                <input
                  type="password"
                  name="azure_client_secret"
                  value={formData.azure_client_secret}
                  onChange={handleChange}
                  placeholder={currentConfig ? '••••••••••••••••' : 'Ingrese el Client Secret'}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
            </div>

            {/* Right Column - Mailbox & Automation */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm border-b pb-2">
                <Inbox size={16} />
                Configuracion del Buzon
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email del Buzon <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="mailbox_email"
                  value={formData.mailbox_email}
                  onChange={handleChange}
                  placeholder="facturas-proveedores@empresa.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carpeta Entrada
                  </label>
                  <input
                    type="text"
                    name="inbox_folder"
                    value={formData.inbox_folder}
                    onChange={handleChange}
                    placeholder="Inbox"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carpeta Procesados
                  </label>
                  <input
                    type="text"
                    name="processed_folder"
                    value={formData.processed_folder}
                    onChange={handleChange}
                    placeholder="Procesados"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </div>
              </div>

              {/* Automation inline */}
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap size={16} className="text-gray-600" />
                    <label htmlFor="auto_process" className="text-sm font-medium text-gray-700">
                      Procesar automaticamente
                    </label>
                    <input
                      type="checkbox"
                      id="auto_process"
                      name="auto_process"
                      checked={formData.auto_process}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary-600 rounded"
                    />
                  </div>
                  <select
                    name="check_interval_minutes"
                    value={formData.check_interval_minutes}
                    onChange={handleChange}
                    className="px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  >
                    <option value={5}>Cada 5 min</option>
                    <option value={10}>Cada 10 min</option>
                    <option value={15}>Cada 15 min</option>
                    <option value={30}>Cada 30 min</option>
                    <option value={60}>Cada hora</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Webhook Section - Full width */}
          {currentConfig && (
            <div className="mt-4 p-3 border rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Radio size={18} className={webhookActive ? 'text-green-600' : 'text-gray-400'} />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Webhook</span>
                    {webhookActive && (
                      <span className="text-xs text-green-600 ml-2">
                        Expira: {dayjs(webhookExpiration).format('DD/MM/YYYY HH:mm')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {webhookActive ? (
                    <>
                      <button
                        onClick={handleCreateWebhook}
                        disabled={creatingWebhook}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                      >
                        {creatingWebhook ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                        Renovar
                      </button>
                      <button
                        onClick={handleDeleteWebhook}
                        disabled={deletingWebhook}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                      >
                        {deletingWebhook ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                        Eliminar
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleCreateWebhook}
                      disabled={creatingWebhook || !formData.webhook_url}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {creatingWebhook ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />}
                      Activar
                    </button>
                  )}
                </div>
              </div>
              <input
                type="text"
                name="webhook_url"
                value={formData.webhook_url}
                onChange={handleChange}
                placeholder="https://tu-dominio.com/webhooks/graph/mail"
                className="w-full px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
              />
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div className={`mt-4 p-3 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="text-green-600" size={18} />
                ) : (
                  <XCircle className="text-red-600" size={18} />
                )}
                <span className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {testResult.message}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
          <div className="flex items-center gap-2">
            {currentConfig && (
              <>
                <button
                  onClick={handleToggle}
                  disabled={toggling}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
                    (currentConfig.isActive ?? currentConfig.is_active)
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {toggling ? <Loader2 size={14} className="animate-spin" /> :
                    (currentConfig.isActive ?? currentConfig.is_active) ? <PowerOff size={14} /> : <Power size={14} />}
                  {(currentConfig.isActive ?? currentConfig.is_active) ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                >
                  {deleting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Delete className="h-5 w-5 text-red-500" />}
                  Eliminar
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleTest}
              disabled={testing || !formData.azure_tenant_id || !formData.azure_client_id}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <TestTube size={14} />}
              Probar
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 border-2 border-green-500 text-green-600 rounded-lg text-sm font-medium hover:bg-green-50 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar
            </button>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Configuración</h2>
            <p className="text-gray-600 mb-4">
              ¿Está seguro que desea eliminar la configuración de email? Se detendrá la importación automática.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleDelete}
                className="px-4 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const resultadoConfig = {
  nueva: { label: 'nueva(s)', color: 'text-green-600' },
  procesada: { label: 'procesada(s)', color: 'text-blue-600' },
  error: { label: 'error', color: 'text-red-600' },
  duplicada: { label: 'duplicada(s)', color: 'text-yellow-600' },
  rechazada: { label: 'rechazada(s)', color: 'text-red-600' },
  omitida: { label: 'omitida(s)', color: 'text-gray-500' }
};

export default function EmailImport() {
  const { currentEmisor } = useAuthStore();
  const [status, setStatus] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);
  const [stats, setStats] = useState({ correos: 0, facturas: 0, errores: 0 });
  const [importaciones, setImportaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [lastUpdated, setLastUpdated] = useState(null);
  const setPageLastUpdated = usePageMetaStore(s => s.setLastUpdated);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem('emailImport_autoRefresh');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [selectedFacturaId, setSelectedFacturaId] = useState(null);
  const [errorModal, setErrorModal] = useState(null);

  const fetchEmailConfig = useCallback(async () => {
    try {
      const response = await gastosApi.getGastosEmailConfig();
      setEmailConfig(response.data.data);
    } catch (error) {
      // No config exists, that's OK
      setEmailConfig(null);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, historyRes] = await Promise.all([
        gastosApi.getEmailImportStatus(),
        gastosApi.getEmailImportHistory({ page: pagination.page, limit: pagination.limit }),
        fetchEmailConfig()
      ]);

      setStatus(statusRes.data.data);
      setStats(statusRes.data.data?.stats || { correos: 0, facturas: 0, errores: 0 });
      setImportaciones(historyRes.data.data || []);
      setPagination(prev => ({ ...prev, ...historyRes.data.pagination }));
      const now = new Date();
      setLastUpdated(now);
      setPageLastUpdated(now);  // header global
    } catch (error) {
      console.error('Error fetching email import data:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, fetchEmailConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds (if enabled)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchData, autoRefresh]);

  const toggleAutoRefresh = () => {
    const newValue = !autoRefresh;
    setAutoRefresh(newValue);
    localStorage.setItem('emailImport_autoRefresh', JSON.stringify(newValue));
  };

  const handleCheckNow = async () => {
    try {
      setChecking(true);
      const response = await gastosApi.checkEmailNow();
      const data = response.data.data;
      toast.success(data?.message || `Revisión completada: ${data?.nuevas || 0} facturas nuevas`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al revisar correos');
    } finally {
      setChecking(false);
    }
  };

  const handleClearHistory = () => {
    setConfirmModal({
      title: '¿Limpiar historial?',
      message: 'Se eliminarán todos los registros del historial de importaciones. Esta acción no se puede deshacer.',
      confirmText: 'Limpiar',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await gastosApi.clearEmailImportHistory();
          toast.success('Historial limpiado');
          setConfirmModal(null);
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al limpiar historial');
        }
      }
    });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const isConnected = status?.connected;
  const webhookActive = status?.webhookActive;

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          {/* "Actualizado: HH:MM:SS" ahora vive en el header global (pageMetaStore) */}
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <label
            className="flex items-center gap-2 cursor-pointer"
            title={autoRefresh ? 'Auto-refresh activo (cada 30s)' : 'Auto-refresh desactivado'}
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={toggleAutoRefresh}
              className="h-4 w-4 text-primary-600 rounded"
            />
            <span className={`text-sm flex items-center gap-1 ${autoRefresh ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
              Auto
              {autoRefresh && (
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              )}
            </span>
          </label>
          <button
            onClick={() => setShowConfigModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border"
          >
            <Settings size={18} />
            Configurar Email
          </button>
        </div>
      </div>

      {/* Company & Connection Status */}
      <div className="bg-white rounded-lg border p-3">
        <div className="flex items-center justify-between gap-4">
          {/* Connection Status */}
          <div className="flex items-center gap-2">
            {isConnected ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                <CheckCircle size={12} />
                Conectado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                <XCircle size={12} />
                Desconectado
              </span>
            )}
            <span className="text-sm text-gray-600">{status?.email || 'No configurado'}</span>
            {webhookActive && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                <Zap size={12} />
                Webhook
              </span>
            )}
          </div>

          {/* Stats inline */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 rounded">
              <span className="text-lg font-bold text-blue-600">{stats.correos}</span>
              <span className="text-xs text-gray-500">correos</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 rounded">
              <span className="text-lg font-bold text-green-600">{stats.facturas}</span>
              <span className="text-xs text-gray-500">facturas</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-red-50 rounded">
              <span className="text-lg font-bold text-red-600">{stats.errores}</span>
              <span className="text-xs text-gray-500">errores</span>
            </div>
          </div>

          {/* Last Check & Check Now */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              {status?.lastCheck ? dayjs(status.lastCheck).format('DD/MM/YYYY, h:mm a') : 'Nunca'}
            </span>
            <button
              onClick={handleCheckNow}
              disabled={checking || !isConnected}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 disabled:opacity-50 text-sm"
            >
              <RefreshCw size={14} className={checking ? 'animate-spin' : ''} />
              Revisar
            </button>
          </div>
        </div>
      </div>

      {/* Not Configured Warning */}
      {!isConnected && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-yellow-800">Email no configurado</p>
            <p className="text-sm text-yellow-700 mt-1">
              Para importar facturas automaticamente desde email, haga clic en{' '}
              <button
                onClick={() => setShowConfigModal(true)}
                className="underline font-medium hover:text-yellow-900"
              >
                Configurar Email
              </button>{' '}
              para conectar su cuenta de Microsoft 365.
            </p>
          </div>
        </div>
      )}

      {/* Inactive Warning */}
      {emailConfig && !(emailConfig.isActive ?? emailConfig.is_active) && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <PowerOff className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-orange-800">Importacion desactivada</p>
            <p className="text-sm text-orange-700 mt-1">
              La importacion automatica de facturas esta desactivada. Haga clic en{' '}
              <button
                onClick={() => setShowConfigModal(true)}
                className="underline font-medium hover:text-orange-900"
              >
                Configurar Email
              </button>{' '}
              para activarla.
            </p>
          </div>
        </div>
      )}

      {/* Import History */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="px-4 py-3 border-2 border-gray-800 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-gray-600" />
            <span className="font-medium text-gray-700">Últimas Importaciones</span>
          </div>
          {importaciones.length > 0 && (
            <button
              onClick={handleClearHistory}
              className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200"
            >
              <Delete className="h-5 w-5 text-red-500" />
              Limpiar
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">TIPO DOC.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Proveedor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Remitente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Asunto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Resultado</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Cargando...
                  </td>
                </tr>
              ) : importaciones.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No hay importaciones registradas
                  </td>
                </tr>
              ) : (
                importaciones.map((imp) => {
                  // Display resultado - already contains the count like "1 nueva(s)"
                  const getResultadoDisplay = () => {
                    // Check if it has "nueva" to color it green
                    const hasNueva = imp.resultado?.includes('nueva');
                    const hasDuplicada = imp.resultado?.includes('duplicada');
                    const hasError = imp.resultado?.includes('error');

                    let colorClass = 'text-gray-700';
                    if (hasNueva && !hasDuplicada) colorClass = 'text-green-600';
                    else if (hasDuplicada && !hasNueva) colorClass = 'text-yellow-600';
                    else if (hasError) colorClass = 'text-red-600';

                    return (
                      <span className={`text-sm font-medium ${colorClass}`}>
                        {imp.resultado || '-'}
                      </span>
                    );
                  };

                  // Get tipo documento label
                  const getTipoDocLabel = () => {
                    const tipos = {
                      'FE': { label: 'FE', color: 'bg-blue-100 text-blue-800' },
                      '08': { label: 'FEC', color: 'bg-purple-100 text-purple-800' },
                      'NC': { label: 'NC', color: 'bg-red-100 text-red-800' },
                      'ND': { label: 'ND', color: 'bg-orange-100 text-orange-800' },
                      'TE': { label: 'TE', color: 'bg-gray-100 text-gray-800' },
                      'PDF': { label: 'PDF', color: 'bg-rose-100 text-rose-800' }
                    };
                    const tipo = tipos[imp.tipoDocumento] || { label: imp.tipoDocumento || '-', color: 'bg-gray-100 text-gray-600' };
                    return (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${tipo.color}`}>
                        {tipo.label}
                      </span>
                    );
                  };

                  return (
                    <tr key={imp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {dayjs(imp.fecha).format('DD/MM/YYYY, h:mm:ss a')}
                      </td>
                      <td className="px-4 py-3">
                        {getTipoDocLabel()}
                      </td>
                      <td className="px-4 py-3">
                        {imp.proveedorNombre ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">{imp.proveedorNombre}</p>
                            <p className="text-xs text-gray-500">{imp.proveedorIdentificacion}</p>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {imp.remitente}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={imp.asunto}>
                        {imp.asunto}
                      </td>
                      <td className="px-4 py-3">
                        {imp.resultado?.includes('rechazada') && imp.error ? (
                          <button
                            onClick={() => setErrorModal(imp)}
                            className="text-sm font-medium text-red-600 hover:text-red-800 hover:underline cursor-pointer"
                          >
                            {imp.resultado}
                          </button>
                        ) : (
                          getResultadoDisplay()
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {imp.facturaId ? (
                          <button
                            onClick={() => setSelectedFacturaId(imp.facturaId)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                          >
                            <Eye size={12} />
                            Ver
                          </button>
                        ) : imp.resultado?.includes('rechazada') && imp.error ? (
                          <button
                            onClick={() => setErrorModal(imp)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50"
                          >
                            <AlertCircle size={12} />
                            Detalle
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-gray-500">
              Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-700">
                Página {pagination.page} de {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Email Configuration Modal */}
      <EmailConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSave={() => {
          fetchEmailConfig();
          fetchData();
        }}
        currentConfig={emailConfig}
      />

      {/* Error Detail Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setErrorModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-red-50 rounded-t-xl">
              <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Documento Rechazado
              </h3>
              <button onClick={() => setErrorModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-gray-500">Proveedor:</span> <strong>{errorModal.proveedorNombre || '-'}</strong></div>
                  <div><span className="text-gray-500">Cédula:</span> {errorModal.proveedorIdentificacion || '-'}</div>
                  <div><span className="text-gray-500">Tipo:</span> {errorModal.tipoDocumento || '-'}</div>
                  <div><span className="text-gray-500">Fecha:</span> {errorModal.fecha ? dayjs(errorModal.fecha).format('DD/MM/YYYY HH:mm') : '-'}</div>
                </div>
                {errorModal.remitente && <div className="mt-1"><span className="text-gray-500">Remitente:</span> {errorModal.remitente}</div>}
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-1">Motivo del rechazo:</p>
                <p className="text-sm text-red-700">{errorModal.error || 'Error desconocido'}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>Acción requerida:</strong> Solicite al emisor que emita una Factura Electrónica (FE) en su lugar.
              </div>
            </div>
            <div className="flex justify-end p-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setErrorModal(null)} className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {selectedFacturaId && (
        <PDFViewerModal
          facturaId={selectedFacturaId}
          onClose={() => setSelectedFacturaId(null)}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirm={confirmModal?.onConfirm}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmText={confirmModal?.confirmText}
        variant={confirmModal?.variant}
      />
    </div>
  );
}
